/**
 * HomeLayoutEditor
 * ─────────────────
 * Bottom-sheet modal that lets the user reorder the primary CTA cards
 * on the /app home feed. Reorder-only (no hide/show) per product decision.
 *
 * Design goals:
 *   • **Obvious but tasteful** — entry from a subtle Sliders icon in
 *     the home header, plus long-press on any card in edit mode. The
 *     sheet uses the same glass surface as the rest of the app.
 *   • **Drag-and-drop via @dnd-kit** — pointer + touch + keyboard.
 *     Touch uses a 200ms delay so scrolling the sheet still works.
 *     `restrictToVerticalAxis` + `restrictToParentElement` prevent
 *     awkward horizontal drift.
 *   • **iOS-safe scroll** — flex column with `min-h-0` on the list
 *     wrapper (learned from DYK/BookmarkPopover fixes).
 *   • **Persists server-side** via /api/settings/home-layout.
 *
 * The parent (`HomePage`) owns the layout state and passes `blocks` +
 * `onSave`. This component mirrors props into local state so drags
 * feel snappy, then calls `onSave` when the user taps Save.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { X, GripVertical, RotateCcw, Check, Sliders } from 'lucide-react';
import { toast } from 'sonner';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useTranslation } from 'react-i18next';

// Human-readable labels + hints for each block id. Kept here so the
// registry lives in ONE place — HomePage renders using this map too.
export const HOME_BLOCK_REGISTRY = {
  'did-you-know': {
    label: 'Did You Know?',
    hint: 'Daily biblical facts & fun discoveries',
  },
  chat: {
    label: 'Chat with Eili',
    hint: 'AI biblical research & conversation',
  },
  reflection: {
    label: 'Daily Reflection',
    hint: "Today's scripture study",
  },
  'study-plans': {
    label: 'Study Plans',
    hint: 'Guided journeys through Scripture',
  },
  community: {
    label: 'Community',
    hint: 'Discussions, prayer & shared reflections',
  },
  reviews: {
    label: 'Reviews',
    hint: 'Rate & share the app',
  },
};

// Default order — MUST stay in sync with backend
// `HOME_LAYOUT_BLOCK_IDS` in app/routers/settings.py.
export const DEFAULT_HOME_LAYOUT = [
  'did-you-know',
  'chat',
  'reflection',
  'study-plans',
  'community',
  'reviews',
];

/**
 * A single sortable row in the layout editor. Kept as a nested
 * component so it can subscribe to the sortable transform hook.
 */
function SortableRow({ id, index }) {
  const meta = HOME_BLOCK_REGISTRY[id];
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: 'var(--glass-bg)',
    borderColor: 'var(--glass-border, #334155)',
    boxShadow: isDragging
      ? '0 12px 32px -12px rgba(0, 0, 0, 0.6)'
      : undefined,
    // Lift the dragged item visually so it clearly separates from
    // the resting rows.
    zIndex: isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.92 : 1,
  };

  if (!meta) return null;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="rounded-xl border p-3 flex items-center gap-3 select-none"
      data-testid={`home-layout-item-${id}`}
    >
      {/* Grab handle — the entire row is draggable via the handle
          (not the whole row) so mobile users can still scroll the
          list by dragging outside the handle. */}
      <button
        type="button"
        className="p-1 -ml-1 rounded touch-none cursor-grab active:cursor-grabbing"
        style={{ color: 'var(--theme-textSecondary)' }}
        aria-label={`Drag ${meta.label}`}
        data-testid={`home-layout-handle-${id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      {/* Position indicator */}
      <span
        className="w-6 h-6 rounded-full text-[11px] font-semibold flex items-center justify-center shrink-0"
        style={{
          background: 'var(--glass-bg-active, #334155)',
          color: 'var(--theme-textSecondary)',
        }}
        aria-hidden="true"
      >
        {index + 1}
      </span>

      {/* Label + hint */}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-semibold truncate"
          style={{ color: 'var(--theme-text)' }}
        >
          {meta.label}
        </p>
        <p
          className="text-xs truncate"
          style={{ color: 'var(--theme-textSecondary)' }}
        >
          {meta.hint}
        </p>
      </div>
    </li>
  );
}

export default function HomeLayoutEditor({ open, onClose, blocks, onSave }) {
  const {
    t
  } = useTranslation();

  const [draft, setDraft] = useState(blocks || DEFAULT_HOME_LAYOUT);
  const [saving, setSaving] = useState(false);

  // Feature-flag gates — hide disabled features from the layout editor
  // itself. When a flag is OFF the block is removed both from the visible
  // list AND from any save payload, so users can't drag it back in.
  const { flags } = useFeatureFlags();
  const BLOCK_FLAG_MAP = useMemo(() => ({
    'study-plans': 'study_plans',
    community: 'community',
  }), []);
  const isBlockEnabled = useCallback((id) => {
    const flagKey = BLOCK_FLAG_MAP[id];
    if (!flagKey) return true; // Unflagged blocks always show.
    return !!flags[flagKey];
  }, [flags, BLOCK_FLAG_MAP]);

  // Visible list = draft filtered by flags.
  const visibleDraft = useMemo(
    () => draft.filter(isBlockEnabled),
    [draft, isBlockEnabled],
  );

  // Re-sync draft whenever the sheet is opened. Don't reset while open
  // — that would blow away in-progress edits.
  useEffect(() => {
    if (open) setDraft(blocks || DEFAULT_HOME_LAYOUT);
  }, [open, blocks]);

  // Sensors — tuned so:
  //   • pointer drags start instantly on desktop (activationConstraint
  //     distance=5px prevents accidental drags on plain clicks)
  //   • touch drags require a 200ms hold before activation so users
  //     can still scroll the sheet body vertically without triggering
  //     a drag (matches iOS 'edit mode' expectations)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const resetToDefault = useCallback(() => {
    setDraft(DEFAULT_HOME_LAYOUT);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(t('generated.homelayouteditor_homescreen_updated_0d42'));
      onClose();
    } catch (e) {
      toast.error(t('generated.homelayouteditor_could_not_save_6ca0'));
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="home-layout-editor"
      role="dialog"
      aria-modal="true"
      aria-labelledby="home-layout-editor-title"
    >
      <div
        className="mt-auto w-full max-w-xl mx-auto rounded-t-2xl overflow-hidden flex flex-col"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, #334155)',
          // --visual-vh is set globally to account for iOS keyboard.
          maxHeight: 'min(85vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grabber pill — iOS-style affordance */}
        <div className="pt-3 pb-1 flex justify-center">
          <span
            className="block w-10 h-1 rounded-full"
            style={{ background: 'var(--glass-border, #334155)' }}
          />
        </div>

        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 border-b shrink-0"
          style={{ borderColor: 'var(--glass-border, #334155)' }}
        >
          <div className="flex items-center gap-2">
            <Sliders className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
            <h2
              id="home-layout-editor-title"
              className="text-base font-semibold"
              style={{ color: 'var(--theme-text)' }}
            >
              {t('generated.homelayouteditor_customize_home_ec11')}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={resetToDefault}
              className="p-1.5 rounded-full transition hover:bg-white/10"
              title={t('generated.homelayouteditor_reset_to_default_c561')}
              aria-label={t('generated.homelayouteditor_reset_to_default_c873')}
              data-testid="home-layout-reset"
            >
              <RotateCcw className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full transition hover:bg-white/10"
              aria-label={t('generated.homelayouteditor_close_2030')}
              data-testid="home-layout-close"
            >
              <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          </div>
        </div>

        {/* Helper text */}
        <p
          className="px-5 pt-3 pb-1 text-xs leading-relaxed shrink-0"
          style={{ color: 'var(--theme-textSecondary)' }}
        >
          {t('generated.homelayouteditor_drag_the_handle_3fd6')}
        </p>

        {/* Sortable list. `min-h-0` is required so the flex child can
            actually engage overflow-y-auto (learned from DYK saved
            panel + BookmarkPopover fixes). */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={visibleDraft} strategy={verticalListSortingStrategy}>
            <ul
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 space-y-2"
              data-testid="home-layout-list"
            >
              {visibleDraft.map((id, idx) => (
                <SortableRow key={id} id={id} index={idx} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>

        {/* Footer actions */}
        <div
          className="px-5 py-3 border-t flex items-center gap-2 shrink-0"
          style={{ borderColor: 'var(--glass-border, #334155)' }}
        >
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-sm font-medium transition hover:bg-white/5"
            style={{
              border: '1px solid var(--glass-border, #334155)',
              color: 'var(--theme-textSecondary)',
            }}
            data-testid="home-layout-cancel"
          >
            {t('generated.homelayouteditor_cancel_f068')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-60 flex items-center justify-center gap-1.5"
            style={{
              background: 'var(--theme-accent)',
              color: 'var(--theme-accent-contrast, #0f172a)',
            }}
            data-testid="home-layout-save"
          >
            <Check className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
